import { useState, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../../store/configure-store'
import { httpService } from '../../services/http-service'
import { SelectCompanyPrompt } from '../../cmps/general/select-company-prompt'
import { EntityTable } from '../../cmps/entity/entity-table'
import { EntityFormModal } from '../../cmps/entity/entity-form-modal'
import { EntityCompleteSetupModal } from '../../cmps/entity/entity-complete-setup-modal'
import { getEntitiesByCompanyNameQuery } from '../../cmps/util-cmps/graphql-queries/entities-query'
import { Spinner } from '../../cmps/util-cmps/spinner'
import { useCachedFetch } from '../../hooks/use-cached-fetch'

export interface EntityHealth {
    hasContext: boolean
    hasCurrentYearAudit: boolean
    hasSystems: boolean
    isComplete: boolean
}

const computeEntityHealth = (entity: any): EntityHealth => {
    const currentYear = new Date().getFullYear()
    const hasContext = !!entity.entityContext
    const hasCurrentYearAudit = (entity.audit || []).some((a: any) => Number(a.year) === currentYear)
    const hasSystems = (entity.entitySystems || []).length > 0
    return { hasContext, hasCurrentYearAudit, hasSystems, isComplete: hasContext && hasCurrentYearAudit && hasSystems }
}

export const EntityPage: React.FC = () => {
    const companyName = useSelector((stateModule: RootState) => stateModule.companyName.value)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [entityToEdit, setEntityToEdit] = useState<any>(null)
    const [setupEntity, setSetupEntity] = useState<any>(null)

    const { data: fetchResult, isLoading, hasError, refetch } = useCachedFetch(
        companyName ? `entities:${companyName}` : null,
        async () => {
            const entitiesQuery = getEntitiesByCompanyNameQuery(companyName)
            const companyQuery = `{ companies(name: "${companyName}") { id name } }`
            const [entitiesResult, companyResult] = await Promise.all([
                httpService.graphql(companyName, entitiesQuery),
                httpService.graphql(companyName, companyQuery),
            ])
            return {
                entities: (entitiesResult.entitiesByCompanyName || []) as any[],
                companyId: companyResult.companies?.[0]?.id as number | null,
            }
        }
    )

    const rawData = fetchResult?.entities || null
    // Company ID from the routed DB (not Redux) so it's correct for silo companies
    const companyId = fetchResult?.companyId ?? null

    const data = useMemo(() => (rawData || []).map((entity: any) => {
        const health = computeEntityHealth(entity)
        return { ...entity, _setupStatus: health.isComplete ? 'Complete' : 'Incomplete', _health: health }
    }), [rawData])

    const incompleteEntities = useMemo(() => data.filter((e: any) => !e._health?.isComplete), [data])

    const openCreateModal = () => {
        setEntityToEdit(null)
        setIsModalOpen(true)
    }

    const openEditModal = (entity: any) => {
        setEntityToEdit(entity)
        setIsModalOpen(true)
    }

    const closeModal = () => {
        setIsModalOpen(false)
        setEntityToEdit(null)
    }

    const handleSuccess = () => {
        closeModal()
        refetch()
    }

    if (!companyName) {
        return <div className="tablePageContainer"><SelectCompanyPrompt /></div>
    }

    return <div className="tablePageContainer">
        <div className="page-header">
            <p className="tableSubtitle">Entities and their context configuration.</p>
            <button className="add-btn" onClick={openCreateModal}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Create Entity
            </button>
            {incompleteEntities.length > 0 && (
                <button className="add-btn" style={{ marginLeft: '8px', backgroundColor: '#d97706' }} onClick={() => setSetupEntity(incompleteEntities[0])}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                        <line x1="12" y1="9" x2="12" y2="13" />
                        <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                    Complete Setup ({incompleteEntities.length})
                </button>
            )}
        </div>

        {isLoading && <Spinner />}
        {!isLoading && !hasError && <EntityTable data={data} onEditClick={openEditModal} onCompleteSetupClick={(entity) => setSetupEntity(entity)} />}
        {!isLoading && hasError && <div className="error-hint">Failed to load data. Try refreshing the page or re-logging in.</div>}

        {isModalOpen && companyId && (
            <EntityFormModal
                key={entityToEdit?.id || 'create'}
                companyName={companyName}
                companyId={companyId}
                entityToEdit={entityToEdit}
                existingEntities={data}
                onSuccess={handleSuccess}
                onClose={closeModal}
            />
        )}

        {setupEntity && (
            <EntityCompleteSetupModal
                companyName={companyName}
                entity={setupEntity}
                health={computeEntityHealth(setupEntity)}
                onSuccess={() => { setSetupEntity(null); refetch() }}
                onClose={() => setSetupEntity(null)}
            />
        )}
    </div>
}
